import { memo, useCallback, useState } from 'react';

import {
  useTrackToggledContentCollectionFormAutoSubmitOff,
  useTrackToggledContentCollectionFormAutoSubmitOn,
} from '@air/analytics';
import { type Form } from '@air/api/types';

import { PermissionToggleInfo } from '~/components/SharePrivateContentModal/shared/PermissionToggleInfo';
import { SharePrivateContentPermissionToggle } from '~/components/SharePrivateContentModal/shared/SharePrivateContentPermissionToggle';
import { useUpdateForm } from '~/swr-hooks/forms/useUpdateForm';

type ContentCollectionAutoSubmitToggleProps = {
  form: Form;
};

export const ContentCollectionAutoSubmitToggle = memo(({ form }: ContentCollectionAutoSubmitToggleProps) => {
  const { updateForm } = useUpdateForm();
  const [isLoading, setIsLoading] = useState(false);

  const { trackToggledContentCollectionFormAutoSubmitOn } = useTrackToggledContentCollectionFormAutoSubmitOn();
  const { trackToggledContentCollectionFormAutoSubmitOff } = useTrackToggledContentCollectionFormAutoSubmitOff();

  const onCheckedChange = useCallback(
    async (checked: boolean) => {
      setIsLoading(true);
      try {
        if (checked) {
          trackToggledContentCollectionFormAutoSubmitOn({
            form_id: form.id,
          });
        } else {
          trackToggledContentCollectionFormAutoSubmitOff({
            form_id: form.id,
          });
        }
        await updateForm({
          ...form,
          autoSubmit: checked,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [form, updateForm, trackToggledContentCollectionFormAutoSubmitOn, trackToggledContentCollectionFormAutoSubmitOff],
  );

  return (
    <SharePrivateContentPermissionToggle
      label="Start uploads immediately for submissions"
      id="autoSubmit"
      isLoading={isLoading}
      checked={Boolean(form.autoSubmit)}
      onCheckedChange={onCheckedChange}
    >
      <PermissionToggleInfo
        text="Files will upload immediately. Users cannot rename, delete, or add metadata."
        className="max-w-[212px]"
      />
    </SharePrivateContentPermissionToggle>
  );
});

ContentCollectionAutoSubmitToggle.displayName = 'ContentCollectionAutoSubmitToggle';
